/**
 * useDirtyFlag -- tiny global registry of "page X has unsaved changes".
 *
 * Background
 * ----------
 * Several pages track unsaved-changes locally:
 *   - planner App.tsx ``hasUnsavedChanges``  (project edits)
 *   - Exclusions.tsx  ``hasUnsavedChanges``  (zone edits)
 *   - others may follow (DesignPlant, DesignGrid, etc.)
 *
 * Before this hook there was no way for a top-bar / shell-level UI
 * (e.g. the global ProjectIndicator) to ask "is anything dirty?"
 * without lifting state into a context provider above each page,
 * which would mean restructuring routing.
 *
 * This hook publishes the dirty state into a module-level registry
 * keyed by an arbitrary string (page name). The ProjectIndicator's
 * "Switch project" / "Clear project" actions consult
 * ``hasAnyDirty()`` / ``getDirtyKeys()`` and pop a confirmation
 * dialog before discarding work.
 *
 * Usage:
 *
 *     // In a page component:
 *     useDirtyFlag('planner', hasUnsavedChanges);
 *
 *     // In the indicator's switcher:
 *     if (hasAnyDirty()) {
 *         const dirty = getDirtyKeys().join(', ');
 *         if (!confirm(`Unsaved changes in ${dirty}; discard?`)) return;
 *     }
 *
 * Cleanup is automatic: when the page unmounts, the hook removes its
 * key from the registry so a stale flag doesn't haunt subsequent
 * navigations.
 *
 * Subscribers: ``subscribe(callback)`` returns an unsubscribe fn.
 * Used by React components that want to RE-RENDER when dirty state
 * changes (e.g. an enabled/disabled style on the indicator).
 */
import { useEffect, useSyncExternalStore } from 'react';

const _registry: Map<string, boolean> = new Map();
const _listeners: Set<() => void> = new Set();

// Cached snapshots so useSyncExternalStore sees stable references when
// the registry hasn't actually changed. Without these, getDirtyKeys()
// would return a freshly-allocated array on every store read and React
// would consider the snapshot "different" every render -> infinite loop.
let _cachedKeys: string[] = [];
let _cachedAnyDirty = false;

function _refreshCache(): void {
  _cachedKeys = Array.from(_registry.keys());
  _cachedAnyDirty = _cachedKeys.length > 0;
}

function _notify(): void {
  _refreshCache();
  for (const cb of _listeners) {
    try { cb(); } catch { /* swallow listener errors */ }
  }
}

/** Register or update a page's dirty flag. Pass false to mark clean. */
export function setDirtyFlag(key: string, isDirty: boolean): void {
  const current = _registry.get(key);
  if (current === isDirty) return;  // no-op, avoid spurious notifies
  if (isDirty) _registry.set(key, true);
  else _registry.delete(key);
  _notify();
}

/** Remove a key entirely from the registry (e.g. on page unmount). */
export function clearDirtyFlag(key: string): void {
  if (!_registry.has(key)) return;
  _registry.delete(key);
  _notify();
}

/** True if any registered page is currently dirty. */
export function hasAnyDirty(): boolean {
  return _cachedAnyDirty;
}

/** List of keys (page names) that are currently dirty. */
export function getDirtyKeys(): string[] {
  return _cachedKeys;
}

/** Subscribe to dirty-state changes. Returns an unsubscribe fn. */
export function subscribeDirty(callback: () => void): () => void {
  _listeners.add(callback);
  return () => { _listeners.delete(callback); };
}

/**
 * Hook: register this page's dirty flag in the global registry.
 *
 * The flag is updated whenever ``isDirty`` changes and is cleared on
 * unmount. ``key`` should be a stable identifier per page (e.g.
 * 'planner', 'exclusions', 'design_plant'); using a per-instance key
 * would prevent the flag from being merged across remounts.
 */
export function useDirtyFlag(key: string, isDirty: boolean): void {
  useEffect(() => {
    setDirtyFlag(key, isDirty);
  }, [key, isDirty]);

  // On unmount, clear the flag so a navigated-away page doesn't hold
  // the registry dirty.
  useEffect(() => {
    return () => { clearDirtyFlag(key); };
  }, [key]);
}

/**
 * Hook: subscribes to the registry and returns the current
 * ``hasAnyDirty()`` value as React state. Components that want to
 * RE-RENDER on dirty changes should use this rather than calling
 * ``hasAnyDirty()`` directly (which is a one-shot read).
 */
export function useAnyDirty(): boolean {
  return useSyncExternalStore(subscribeDirty, hasAnyDirty, hasAnyDirty);
}

/** Hook: same idea, returns the list of dirty keys. */
export function useDirtyKeys(): string[] {
  return useSyncExternalStore(subscribeDirty, getDirtyKeys, getDirtyKeys);
}
